import { Box, Text } from "@chakra-ui/react";
import { FundFamilyStateBadge } from "features/fund-family/FundFamilyStateBadge";
import { useCreditFacilityListSuspense } from "gen/endpoints/credit-facility/credit-facility";
import { useFundFamilyRetrieveSuspense } from "gen/endpoints/fund-family/fund-family";
import { FundFamilyStateMachineFieldsEnum } from "gen/model/fundFamilyStateMachineFieldsEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { compact } from "lodash-es";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import PageSidebar from "shared/components/layout/PageSidebar";
import { NavigationItem } from "shared/components/navbar/NavigationItem";
import SidebarNav from "shared/components/navbar/side/SideNavBar";
import { useFeatureFlagsActive } from "shared/hooks/useFeatureFlag";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { useHasPermission } from "shared/utils/PermissionUtils";

export interface FundFamilySidebarProps {
  showSidebar: boolean;
}

export const FundFamilySidebar: React.FC<FundFamilySidebarProps> = ({
  showSidebar,
}) => (
  <PageSidebar defaultIsPersistent={showSidebar}>
    <Box width="100%">
      <QueryBoundary>
        <FundFamilySidebarContent />
      </QueryBoundary>
    </Box>
  </PageSidebar>
);

const FundFamilySidebarContent: React.FC = () => {
  const { t } = useTranslation(["common", "fundFamily"]);
  const { fundFamilyId } = useRequiredStringParams();

  const { data } = useFundFamilyRetrieveSuspense(fundFamilyId);
  const { data: creditFacilityList } =
    useCreditFacilityListSuspense(fundFamilyId);

  const fundFamilyName = data.name;
  const isDraft = data.state === FundFamilyStateMachineFieldsEnum.draft;
  const featureFlagValues = useFeatureFlagsActive([
    "fund_family_setup",
    "use_allocation_rule_groups",
    "allow_creation_of_allocation_rule_graphs_via_ui",
  ]);
  const isServiceUser = useHasPermission(PermissionTypeEnum.service);

  const isCreditFacilityConfigured =
    creditFacilityList[0] && creditFacilityList[0].state !== "draft";

  const fundFamilyRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId });

  const navItems = useMemo((): NavigationItem[] => {
    const fundFamilySetupRoute = fundFamilyRoute.setup({});

    if (isDraft && featureFlagValues.get("fund_family_setup")) {
      return [
        {
          id: "fund-family",
          name: t("common:fundFamily"),
          href: fundFamilySetupRoute.fund({}).$,
        },
        {
          id: "entities",
          name: t("common:entities"),
          href: fundFamilySetupRoute.entities({}).$,
        },
        {
          id: "classes",
          name: t("common:classes"),
          href: fundFamilySetupRoute.classes({}).$,
        },
        {
          id: "commitments",
          name: t("common:commitments"),
          href: fundFamilySetupRoute.commitments({}).$,
        },
        {
          id: "capital-activity",
          name: t("common:closing"),
          href: fundFamilySetupRoute.closing({}).$,
        },
      ];
    }

    return compact([
      {
        id: "summary",
        name: t("fundFamily:summary.navTitle"),
        href: fundFamilyRoute.summary({}).$,
      },
      {
        id: "commitments",
        name: t("common:commitments"),
        href: fundFamilyRoute.commitments({}).$,
      },
      {
        id: "capital-activity",
        name: t("common:capitalActivity"),
        href: fundFamilyRoute.capitalActivity({}).$,
      },
      {
        id: "fees",
        name: t("common:fees"),
        href: fundFamilyRoute.fees({}).$,
      },
      {
        id: "investments",
        name: t("common:investments"),
        href: fundFamilyRoute.investments({}).$,
      },
      isCreditFacilityConfigured && {
        id: "credit-facilities",
        name: t("common:creditFacility"),
        href: fundFamilyRoute.creditFacilities({}).index({}).$,
      },
      {
        id: "financial-reporting",
        name: t("common:financialReporting"),
        href: fundFamilyRoute.financialReporting({}).index({}).$,
      },
      {
        id: "configuration",
        name: t("common:configuration"),
        href: "",
        items: compact([
          {
            id: "config-fund-family",
            name: t("common:fundFamily"),
            href: fundFamilySetupRoute.fund({}).$,
          },
          !(
            featureFlagValues.get("use_allocation_rule_groups") ||
            featureFlagValues.get(
              "allow_creation_of_allocation_rule_graphs_via_ui",
            )
          ) && {
            id: "config-entities",
            name: t("common:entities"),
            href: fundFamilySetupRoute.entities({}).$,
          },
          (featureFlagValues.get("use_allocation_rule_groups") ||
            featureFlagValues.get(
              "allow_creation_of_allocation_rule_graphs_via_ui",
            )) && {
            id: "config-fund-structure",
            name: t("common:fundStructure"),
            href: fundFamilySetupRoute.fundStructure({}).$,
          },
          {
            id: "config-classes",
            name: t("common:classes"),
            href: fundFamilySetupRoute.classes({}).$,
          },
          isServiceUser && {
            id: "config-notices",
            name: t("common:notices"),
            href: fundFamilySetupRoute.notices({}).$,
          },
          {
            id: "config-credit-facilities",
            name: t("common:creditFacility"),
            href: fundFamilySetupRoute.creditFacilities({}).$,
          },
        ]),
      },
    ]);
  }, [
    fundFamilyRoute,
    isDraft,
    featureFlagValues,
    t,
    isCreditFacilityConfigured,
    isServiceUser,
  ]);

  return (
    <>
      <Box
        borderBottomWidth={1}
        borderColor="gray.200"
        px="24px"
        py="16px"
        mb="8px"
        bg="white"
      >
        <Text textStyle="h5">{fundFamilyName}</Text>
        <Text textStyle="small" color="gray.500" mt="18px">
          {t("fundFamily:fundPhase")}
        </Text>
        {data.state && <FundFamilyStateBadge state={data.state} mt="4px" />}
      </Box>
      <SidebarNav items={navItems} />
    </>
  );
};
